import cx from 'classnames'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { match } from '@/utils/pattern-match-util'
import { useInfoPanelContext } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/InfoPanel/InfoPanelContext'
import styles from './InfoPanel.module.css'
import { ModalDialogContent, ModalDialogHeader } from '@/components/ModalDialog'
import { FullscreenModal } from '@/components/FullscreenModal'
import type { ReactNode } from 'react'
import { StyledLink } from '@/components/Link'
import { Heading } from 'react-aria-components'
import { Prose } from '@/components/Prose'

interface InfoPanelTitleProps {
  children: ReactNode
  'aria-label'?: string
}

export function InfoPanelTitle({ children, ...rest }: InfoPanelTitleProps) {
  return (
    <Heading {...rest} slot="title" level={1} className={styles.title}>
      {children}
    </Heading>
  )
}

interface InfoPanelDescriptionProps {
  children: ReactNode
}

export function InfoPanelDescription({ children }: InfoPanelDescriptionProps) {
  return <Prose>{children}</Prose>
}

interface InfoPanelFooterProps {
  children?: ReactNode
}

export function InfoPanelFooter({ children }: InfoPanelFooterProps) {
  return <div className={styles.footer}>{children}</div>
}

interface InfoPanelLinksProps {
  title?: string
  links: { label: string; url: string }[]
}

export function InfoPanelLinks({ title, links }: InfoPanelLinksProps) {
  if (links.length === 0) {
    return null
  }

  return (
    <>
      <Heading slot="title" level={2} className={styles.linksLabel}>
        {title || 'IRS resources for more info:'}
      </Heading>
      <ul className={styles.linksList}>
        {links.map((link, index) => (
          <li key={index}>
            <StyledLink href={link.url} target="_blank">
              {link.label}
            </StyledLink>
          </li>
        ))}
      </ul>
    </>
  )
}

interface InfoPanelProps {
  variant: 'inline' | 'modal'
  testId?: string
}

export function InfoPanel({ variant, testId }: InfoPanelProps) {
  const {
    activeContent: ActiveContent,
    isOpen,
    hideInfoPanel,
  } = useInfoPanelContext()

  if (!isOpen) return null

  const headerContent = (
    <div className={styles.headerContent}>
      <Icon name="info" variant="active" />
      <IconButton
        sm
        name="close"
        label="Close info panel"
        onPress={hideInfoPanel}
        testId={testId ? `${testId}-close-button` : undefined}
      />
    </div>
  )

  return match(variant, {
    modal: () => (
      <FullscreenModal isOpen={isOpen} aria-label="Info panel" testId={testId}>
        <ModalDialogHeader>{headerContent}</ModalDialogHeader>
        <ModalDialogContent>
          <div className={styles.modalContent}>
            {ActiveContent && <ActiveContent />}
          </div>
        </ModalDialogContent>
      </FullscreenModal>
    ),
    inline: () => (
      <aside
        className={cx(styles.root, styles.inline, isOpen && styles.open)}
        data-test={testId}
        aria-hidden={!isOpen}
      >
        <header className={styles.header}>{headerContent}</header>
        <div className={styles.content}>
          {ActiveContent && <ActiveContent />}
        </div>
      </aside>
    ),
  })
}

export * from './InfoPanelContext'
